import { ACTIONS } from './rbac.js'

/**
 * TOP TIER TEAMS — the curated registry of SR's elite / priority accounts.
 *
 * This is a DIRECTORY, not a system of record. Every entry is a set of links
 * into surfaces that already exist and already own their data: the customer
 * ring-selection portal, the presentation film, and the internal ring pricing /
 * publish / tracking console. Nothing here embeds those surfaces, mirrors their
 * state, or carries a single line of pricing or cost logic — the hub's job is to
 * be the front door for the account, and the linked app stays the source of
 * truth for what it does.
 *
 * ADDING A TEAM: append an object to `TOP_TIER_TEAMS`. The page is driven
 * entirely off this array — the featured slot is whichever entry carries
 * `featured: true` (the first one wins), the rest render into the roster grid.
 * No component change is needed to add, reorder, or retire a team.
 *
 * A word on crests: we deliberately do NOT hot-link a club's logo from its own
 * site. A dead external link poisons the record, and a borrowed mark is not an
 * owned asset. A team renders as SR-set type plus its accent color until an
 * OWNED, SR-hosted crest exists — at which point set `crestUrl` to that hosted
 * asset and the card will use it.
 */

/** Link audiences. Drives the tag, the styling, and the RBAC gate. */
export const AUDIENCE = {
  /** Something we send to the customer. Safe to share, no internal numbers. */
  CUSTOMER: 'customer',
  /** Staff-only console. Never put one of these in a customer email. */
  INTERNAL: 'internal',
}

/** Tier labels. `Flagship` gets the premium treatment on the page. */
export const TIER = {
  FLAGSHIP: 'Flagship',
  TOP_TIER: 'Top Tier',
}

export const TOP_TIER_TEAMS = [
  {
    id: 'carolina-hurricanes',
    name: 'Carolina Hurricanes',
    league: 'NHL',
    location: 'Raleigh, North Carolina',
    tier: TIER.FLAGSHIP,
    featured: true,
    /** Where the account sits right now, in plain language. */
    status: 'Ring selection live',
    program: 'Championship ring program',
    /** Club accent, used for the card rail only — never as a page theme. */
    accent: '#ce1126',
    /** OWNED, SR-hosted crest. Null until one exists; never an external URL. */
    crestUrl: null,
    /**
     * The featured visual for the account — an OWNED, SR-hosted photograph
     * served out of this repo's `public/`, never hot-linked. Same discipline as
     * `crestUrl`: no external URL ever goes in this field.
     */
    heroImage: {
      src: '/images/hurricanes/championship-rings-01-2000.jpg',
      /** Small variant for the compact roster card — same owned photograph. */
      thumbSrc: '/images/hurricanes/championship-rings-01-800.jpg',
      alt: "The Carolina Hurricanes championship rings produced by Southern Recognition",
      caption: 'The championship ring program, as produced.',
    },
    summary:
      'The flagship Top Tier account. The club selects its ring through a dedicated portal, the program is presented through its own screening film, and pricing, publishing and view tracking are run from the internal rings console.',
    links: [
      {
        id: 'ring-portal',
        label: 'Ring Selection Portal',
        href: 'https://present.srchamp.com/hurricanes-rings',
        audience: AUDIENCE.CUSTOMER,
        blurb:
          'The customer-facing portal where the club reviews options and makes its ring selection.',
      },
      {
        id: 'presentation-film',
        label: 'Presentation Film',
        href: 'https://present.srchamp.com/hurricanes',
        audience: AUDIENCE.CUSTOMER,
        blurb:
          'The screening film built for this account, on the standard presentation portal.',
      },
      {
        id: 'rings-admin',
        label: 'Ring Pricing, Publish & Tracking',
        href: 'https://present.srchamp.com/rings-admin',
        audience: AUDIENCE.INTERNAL,
        action: ACTIONS.TOPTIER_ADMIN_VIEW,
        blurb:
          'Internal console: set and publish ring pricing to the portal, and track what the club has viewed and selected. Never share this link with a customer.',
      },
    ],
  },
]

/** The featured entry (the flagship), or null if none is flagged. */
export function featuredTeam(teams = TOP_TIER_TEAMS) {
  return teams.find((t) => t.featured) || teams[0] || null
}

/** Everything that is not the featured entry, in declaration order. */
export function rosterTeams(teams = TOP_TIER_TEAMS) {
  const hero = featuredTeam(teams)
  return teams.filter((t) => t !== hero)
}

/** `https://present.srchamp.com/hurricanes-rings` → `present.srchamp.com/hurricanes-rings` */
export function displayUrl(href) {
  return String(href || '').replace(/^https?:\/\//, '').replace(/\/$/, '')
}
